import type { ClubEventRow } from "@/api/club/ClubApi";
import { useTheme } from "@/theme/ThemeProvider";
import { DenseRow } from "@/ui/components/DenseRow";
import { EmptyState } from "@/ui/components/EmptyState";
import { StatusChip } from "@/ui/components/StatusChip";
import { formatAgendaWhenDE, formatLongDateDE } from "@/utils/isoDate";
import React, { useMemo } from "react";
import { Text, View } from "react-native";

function formatWhen(ev: ClubEventRow): string {
  return formatAgendaWhenDE(ev.starts_at, !!ev.all_day);
}

export function CalendarAgenda({
  events,
  onPressEvent,
}: {
  events: ClubEventRow[];
  onPressEvent: (event: ClubEventRow) => void;
}) {
  const { theme } = useTheme();

  const sorted = useMemo(
    () =>
      [...events].sort(
        (a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at)
      ),
    [events]
  );

  if (sorted.length === 0) {
    return (
      <EmptyState
        title="Keine Termine in diesem Zeitraum."
        hint="Wechsle den Monat oder lege einen neuen Termin an."
      />
    );
  }

  let lastDay = "";
  const rows: React.ReactNode[] = [];

  for (const ev of sorted) {
    const day = formatLongDateDE(ev.starts_at);
    if (day !== lastDay) {
      lastDay = day;
      rows.push(
        <Text
          key={`d-${day}`}
          style={{
            color: theme.color.muted,
            fontSize: theme.fontSize.tiny,
            fontWeight: "700",
            letterSpacing: 0.5,
            textTransform: "uppercase",
            marginTop: theme.space.md,
            paddingHorizontal: theme.space.md,
            paddingBottom: theme.space.xs,
          }}
        >
          {day}
        </Text>
      );
    }
    rows.push(
      <DenseRow
        key={ev.item_id}
        title={ev.title}
        meta={[
          formatWhen(ev),
          ev.location_text,
          ev.cancelled_at ? "Abgesagt" : null,
        ]
          .filter(Boolean)
          .join(" · ")}
        trailing={
          ev.cancelled_at ? (
            <StatusChip label="Abgesagt" tone="danger" size="sm" />
          ) : undefined
        }
        onPress={() => onPressEvent(ev)}
        style={ev.cancelled_at ? { opacity: 0.75 } : undefined}
      />
    );
  }

  return <View>{rows}</View>;
}
